/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Modal, ModalProps, Steps, Button, message, Alert } from 'antd';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { useProjectId } from '~src/hooks/useProjectId';
import { useTemplateArchiveFolderStructure } from '~src/hooks/api-hooks/useProjectBuilderModal';
import { TemplateSelectionStep } from './steps/TemplateSelectionStep';
import { FolderSelectionStep } from './steps/FolderSelectionStep';
import { ReviewSelectionStep } from './steps/ReviewSelectionStep';
import { ZipUploadStep } from './steps/ZipUploadStep';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { DbFolder } from '~src/types/db';
import {
  initializeArchiveFolderStructure,
  syncArchiveFolderStructure,
} from '~src/services/api/projectBuilder';
import { queryCache } from 'react-query';

export interface ImportTemplateModalProps extends Partial<ModalProps> {
  onCancel?: () => void;
  mode?: 'project-builder' | 'archive' | 'project-builder-folder';
  targetFolderId?: number;
  targetFolderName?: string;
}

export const ImportTemplateModal: React.FC<ImportTemplateModalProps> = ({
  onCancel,
  mode = 'archive',
  targetFolderId,
  targetFolderName,
  ...modalProps
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const projectId = useProjectId();

  const [currentStep, setCurrentStep] = useState(0);
  const [importSource, setImportSource] = useState<'template' | 'zip'>(
    'template'
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  );
  const [selectedFolderIds, setSelectedFolderIds] = useState<Set<number>>(
    new Set()
  );
  const [zipFolderPaths, setZipFolderPaths] = useState<string[]>([]);
  const [selectedZipPaths, setSelectedZipPaths] = useState<Set<string>>(
    new Set()
  );
  const [isInitializing, setIsInitializing] = useState(false);

  const templateFoldersQuery = useTemplateArchiveFolderStructure(
    projectId!,
    selectedTemplateId,
    !!selectedTemplateId
  );

  const handleTemplateSelect = (templateId: string) => {
    setImportSource('template');
    setSelectedTemplateId(templateId);
    setSelectedFolderIds(new Set());
    setCurrentStep(1);
  };

  const handleZipUpload = () => {
    setImportSource('zip');
    setCurrentStep(1);
  };

  const handleZipProcessed = (paths: string[]) => {
    setZipFolderPaths(paths);
  };

  const handleToggleFolderSelection = (
    folderId: number,
    isChecked: boolean
  ) => {
    setSelectedFolderIds((prev) => {
      const newSet = new Set(prev);

      if (isChecked) {
        // Only add the folder itself (descendants handled in CheckboxFolderTree)
        newSet.add(folderId);
      } else {
        // Remove the folder
        newSet.delete(folderId);
      }

      return newSet;
    });
  };

  const getAllChildIds = (parentId: number, folders: DbFolder[]): number[] => {
    const ids = [parentId];
    const children = folders.filter((f) => f.parent_id === parentId);
    children.forEach((child) => {
      ids.push(...getAllChildIds(child.id, folders));
    });
    return ids;
  };

  const handleRemoveFolder = (folderId: number) => {
    setSelectedFolderIds((prev) => {
      const newSet = new Set(prev);
      if (templateFoldersQuery.data) {
        const idsToRemove = getAllChildIds(folderId, templateFoldersQuery.data);
        idsToRemove.forEach((id) => newSet.delete(id));
      } else {
        newSet.delete(folderId);
      }
      return newSet;
    });
  };

  const handleNext = () => {
    if (currentStep === 0) {
      if (importSource === 'template' && !selectedTemplateId) {
        message.warning(
          intl.formatMessage({ id: 'no-template-selected-import' })
        );
        return;
      }
    }
    if (currentStep === 1) {
      if (importSource === 'template' && selectedFolderIds.size === 0) {
        message.warning(intl.formatMessage({ id: 'no-folders-selected' }));
        return;
      }
      if (importSource === 'zip' && selectedZipPaths.size === 0) {
        message.warning(intl.formatMessage({ id: 'no-folders-selected' }));
        return;
      }
    }
    setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    const prevStep = currentStep - 1;
    if (prevStep === 0) {
      setImportSource('template');
      setSelectedTemplateId(null);
      setSelectedFolderIds(new Set());
      setZipFolderPaths([]);
      setSelectedZipPaths(new Set());
    }
    setCurrentStep(prevStep);
  };

  // Build hierarchical folder structure for initialize API
  const buildFolderHierarchy = (
    selectedFolders: DbFolder[],
    allFolders: DbFolder[]
  ): any[] => {
    // Build recursive sub_folder structure
    const buildSubFolders = (parentId: number): any[] | null => {
      const children = selectedFolders.filter((f) => f.parent_id === parentId);
      if (children.length === 0) return null;

      return children.map((child) => ({
        name: child.name,
        description: child.description || '',
        sub_folder: buildSubFolders(child.id),
      }));
    };

    // Get root folders (parent_id === null)
    const rootFolders = selectedFolders.filter((f) => f.parent_id === null);

    return rootFolders.map((root) => ({
      name: root.name,
      description: root.description || '',
      sub_folder: buildSubFolders(root.id),
    }));
  };

  // Build hierarchical folder structure from ZIP paths for initialize API
  const buildFolderHierarchyFromPaths = (selectedPaths: string[]): any[] => {
    // Sort paths by depth
    const sortedPaths = [...selectedPaths].sort((a, b) => {
      const depthA = a.split('/').length;
      const depthB = b.split('/').length;
      return depthA - depthB;
    });

    // Build a map of path -> folder object with children
    const pathMap = new Map<string, any>();

    sortedPaths.forEach((path) => {
      const segments = path.split('/').filter(Boolean);
      const name = segments[segments.length - 1];
      const parentPath =
        segments.length > 1 ? segments.slice(0, -1).join('/') : null;

      const folderObj = {
        name,
        description: '',
        sub_folder: null,
      };

      pathMap.set(path, folderObj);

      // If this path has a parent in selectedPaths, add it as a child
      if (parentPath && selectedPaths.includes(parentPath)) {
        const parent = pathMap.get(parentPath);
        if (parent) {
          if (!parent.sub_folder) {
            parent.sub_folder = [];
          }
          parent.sub_folder.push(folderObj);
        }
      }
    });

    // Return only root-level folders (those without parents in selectedPaths)
    return sortedPaths
      .filter((path) => {
        const segments = path.split('/');
        if (segments.length === 1) return true; // Root level
        const parentPath = segments.slice(0, -1).join('/');
        return !selectedPaths.includes(parentPath); // Parent not in selection
      })
      .map((path) => pathMap.get(path))
      .filter(Boolean);
  };

  const handleImport = () => {
    if (importSource === 'zip') {
      // ZIP mode: check if archive or project-builder
      if (mode === 'archive') {
        // Archive mode: use INIT/SYNC APIs
        const folderStructure = buildFolderHierarchyFromPaths(
          Array.from(selectedZipPaths)
        );

        setIsInitializing(true);

        (async () => {
          try {
            await initializeArchiveFolderStructure({
              projectId: projectId!,
              folderStructure,
            });

            // Invalidate folder queries to refresh the archive table
            await queryCache.invalidateQueries('folders');
            await queryCache.invalidateQueries('project-builder');

            // Wait a bit for queries to refetch before closing
            await new Promise((resolve) => setTimeout(resolve, 300));

            message.success(
              intl.formatMessage({ id: 'archive-initialized-successfully' })
            );

            handleClose();
          } catch (error: any) {
            console.error('Failed to initialize archive from ZIP:', error);

            // Check if error is 400 (folders already exist) - fallback to sync API
            if (error?.response?.status === 400) {
              try {
                // Add parent_id: null to root folders for sync API compatibility
                const folderStructureForSync = folderStructure.map(
                  (rootFolder) => ({
                    ...rootFolder,
                    parent_id: null,
                  })
                );

                // Retry with sync API
                await syncArchiveFolderStructure({
                  projectId: projectId!,
                  updates: [],
                  creates: folderStructureForSync,
                });

                // Invalidate folder queries to refresh the archive table
                await queryCache.invalidateQueries('folders');
                await queryCache.invalidateQueries('project-builder');

                // Wait a bit for queries to refetch before closing
                await new Promise((resolve) => setTimeout(resolve, 300));

                message.success(
                  intl.formatMessage({ id: 'archive-initialized-successfully' })
                );

                handleClose();
                return;
              } catch (syncError) {
                console.error('Failed to sync archive from ZIP:', syncError);
                message.error(
                  intl.formatMessage({ id: 'failed-to-initialize-archive' })
                );
                setIsInitializing(false);
                return;
              }
            } else {
              message.error(
                intl.formatMessage({ id: 'failed-to-initialize-archive' })
              );
            }
            setIsInitializing(false);
          }
        })();

        return;
      } else {
        // Project Builder mode: dispatch event with selected paths
        window.dispatchEvent(
          new CustomEvent('importZipFolders', {
            detail: {
              selectedPaths: Array.from(selectedZipPaths),
              targetFolderId:
                mode === 'project-builder-folder' ? targetFolderId : null,
            },
          })
        );

        // Show success message
        message.success(
          intl.formatMessage(
            { id: 'import-zip-success' },
            { count: selectedZipPaths.size }
          )
        );

        // Close modal
        handleClose();
        return;
      }
    }

    if (mode === 'project-builder' || mode === 'project-builder-folder') {
      // Project Builder mode: dispatch event with selected folders
      if (!templateFoldersQuery.data) return;

      const selectedFolders = templateFoldersQuery.data.filter((folder) =>
        selectedFolderIds.has(folder.id)
      );

      // Dispatch custom event with folder hierarchy
      window.dispatchEvent(
        new CustomEvent('importTemplateFolders', {
          detail: {
            folders: selectedFolders,
            templateId: selectedTemplateId,
            targetFolderId:
              mode === 'project-builder-folder' ? targetFolderId : null,
          },
        })
      );

      // Show success message
      message.success(
        intl.formatMessage({ id: 'template-folders-imported-success' })
      );

      // Close modal
      handleClose();
    } else {
      // Archive mode: initialize archive folder structure
      if (!templateFoldersQuery.data) {
        message.error(intl.formatMessage({ id: 'template-data-not-loaded' }));
        return;
      }

      const selectedFolders = templateFoldersQuery.data.filter((folder) =>
        selectedFolderIds.has(folder.id)
      );

      const folderStructure = buildFolderHierarchy(
        selectedFolders,
        templateFoldersQuery.data || []
      );

      setIsInitializing(true);

      (async () => {
        try {
          await initializeArchiveFolderStructure({
            projectId: projectId!,
            folderStructure,
          });

          // Invalidate folder queries to refresh the archive table
          await queryCache.invalidateQueries('folders');
          await queryCache.invalidateQueries('project-builder');

          // Wait a bit for queries to refetch before closing
          await new Promise((resolve) => setTimeout(resolve, 300));

          message.success(
            intl.formatMessage({ id: 'archive-initialized-successfully' })
          );

          handleClose();
        } catch (error: any) {
          console.error('Failed to initialize archive:', error);

          // Check if error is 400 (folders already exist) - fallback to sync API
          if (error?.response?.status === 400) {
            try {
              // Add parent_id: null to root folders for sync API compatibility
              const folderStructureForSync = folderStructure.map(
                (rootFolder) => ({
                  ...rootFolder,
                  parent_id: null,
                })
              );

              // Retry with sync API - updates empty, creates is the folder structure with hierarchy preserved
              await syncArchiveFolderStructure({
                projectId: projectId!,
                updates: [],
                creates: folderStructureForSync,
              });

              // Invalidate folder queries to refresh the archive table
              await queryCache.invalidateQueries('folders');
              await queryCache.invalidateQueries('project-builder');

              // Wait a bit for queries to refetch before closing
              await new Promise((resolve) => setTimeout(resolve, 300));

              message.success(
                intl.formatMessage({ id: 'archive-initialized-successfully' })
              );

              handleClose();
              return; // Exit successfully after sync
            } catch (syncError) {
              console.error('Failed to sync archive:', syncError);
              message.error(
                intl.formatMessage({ id: 'archive-initialize-failed' })
              );
            }
          } else {
            // Non-400 error, show generic error
            message.error(
              intl.formatMessage({ id: 'archive-initialize-failed' })
            );
          }
        } finally {
          setIsInitializing(false);
        }
      })();
    }
  };

  const handleClose = () => {
    setCurrentStep(0);
    setImportSource('template');
    setSelectedTemplateId(null);
    setSelectedFolderIds(new Set());
    setZipFolderPaths([]);
    setSelectedZipPaths(new Set());
    onCancel?.();
  };

  const steps =
    importSource === 'zip'
      ? [
          {
            title: intl.formatMessage({ id: 'select-template' }),
            content: (
              <TemplateSelectionStep
                selectedTemplateId={selectedTemplateId}
                onTemplateSelect={handleTemplateSelect}
                onZipUpload={handleZipUpload}
                mode={mode}
              />
            ),
          },
          {
            title: intl.formatMessage({ id: 'upload-zip-template' }),
            content: (
              <ZipUploadStep
                selectedPaths={selectedZipPaths}
                onPathsChange={setSelectedZipPaths}
                onZipProcessed={handleZipProcessed}
              />
            ),
          },
        ]
      : [
          {
            title: intl.formatMessage({ id: 'select-template' }),
            content: (
              <TemplateSelectionStep
                selectedTemplateId={selectedTemplateId}
                onTemplateSelect={handleTemplateSelect}
                onZipUpload={handleZipUpload}
                mode={mode}
              />
            ),
          },
          {
            title: intl.formatMessage({ id: 'select-folders' }),
            content: (
              <FolderSelectionStep
                templateId={selectedTemplateId}
                selectedFolderIds={selectedFolderIds}
                onToggleFolderSelection={handleToggleFolderSelection}
              />
            ),
          },
          ...(mode === 'archive'
            ? [
                {
                  title: intl.formatMessage({ id: 'review-selection' }),
                  content: (
                    <ReviewSelectionStep
                      projectId={projectId!}
                      templateId={selectedTemplateId}
                      selectedFolderIds={selectedFolderIds}
                      onRemoveFolder={handleRemoveFolder}
                    />
                  ),
                },
              ]
            : []),
        ];

  const maxStep = importSource === 'zip' ? 1 : mode === 'archive' ? 2 : 1;

  return (
    <Modal
      title={
        <ModalTitle
          text={
            mode === 'project-builder-folder' && targetFolderName
              ? intl.formatMessage(
                  { id: 'import-template-to-folder' },
                  { folderName: targetFolderName }
                )
              : intl.formatMessage({ id: 'import-template' })
          }
          icon={['far', 'folder-upload']}
        />
      }
      width={900}
      centered
      onCancel={handleClose}
      maskClosable={false}
      closable={true}
      footer={
        <div css={styles.footer}>
          {currentStep > 0 && (
            <Button onClick={handleBack}>
              {intl.formatMessage({ id: 'back' })}
            </Button>
          )}
          <div css={styles.footerRight}>
            {currentStep < maxStep && (
              <Button
                type="primary"
                onClick={handleNext}
                disabled={
                  currentStep === 0 &&
                  importSource === 'template' &&
                  !selectedTemplateId
                }>
                {intl.formatMessage({ id: 'next' })}
              </Button>
            )}
            {currentStep === maxStep && (
              <Button
                type="primary"
                onClick={handleImport}
                disabled={
                  (importSource === 'template' &&
                    selectedFolderIds.size === 0) ||
                  (importSource === 'zip' && selectedZipPaths.size === 0) ||
                  isInitializing
                }
                loading={isInitializing}>
                {mode === 'project-builder' || mode === 'project-builder-folder'
                  ? intl.formatMessage({ id: 'import-to-project-builder' })
                  : intl.formatMessage({ id: 'import' })}
              </Button>
            )}
          </div>
        </div>
      }
      {...modalProps}>
      <Steps current={currentStep} css={styles.steps}>
        {steps.map((step) => (
          <Steps.Step key={step.title} title={step.title} />
        ))}
      </Steps>
      {mode === 'project-builder-folder' && targetFolderName && (
        <div css={styles.alertWrapper}>
          <Alert
            type="info"
            message={intl.formatMessage(
              { id: 'template-will-import-to-folder' },
              { folderName: targetFolderName }
            )}
            showIcon
          />
        </div>
      )}
      <div css={styles.content}>{steps[currentStep].content}</div>
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  steps: {
    marginBottom: 32,
    '.ant-steps-item-process .ant-steps-item-icon': {
      backgroundColor: theme.colors.primary,
      borderColor: theme.colors.primary,
    },
    '.ant-steps-item-finish .ant-steps-item-icon': {
      borderColor: theme.colors.primary,
      '& .ant-steps-icon': {
        color: theme.colors.primary,
      },
    },
  },
  alertWrapper: {
    marginTop: '10px !important',
  },
  content: {
    minHeight: 520,
    marginTop: 24,
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  footerRight: {
    display: 'flex',
    gap: 8,
    marginLeft: 'auto',
  },
});
